/**
 * StadiumWalletTopupWidget
 * ────────────────────────
 * "Pre-load your match-day wallet during ticket checkout" preview on
 * the HustleStadium sales page. Calls
 * `/api/stadium/wallet-topup-preview` which returns line-items,
 * merchant surcharge, promo credit and the optional first-drink
 * voucher. Anonymous — no login required.
 */
import { useEffect, useState } from "react";
import { HandCoins, Wallet, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  || (typeof window !== "undefined" ? window.location.origin : "");

const AMOUNT_PRESETS = [50, 100, 200, 350];

export default function StadiumWalletTopupWidget() {
  const [amount, setAmount] = useState(100);
  const [includeDrink, setIncludeDrink] = useState(true);
  const [promo, setPromo] = useState("MATCHDAY10");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/stadium/wallet-topup-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount_aud: amount,
          include_first_drink: includeDrink,
          promo_code: promo || null,
        }),
      });
      if (r.ok) setPreview(await r.json());
    } catch {
      /* soft-fail — sales page keeps working without the preview */
    }
    setBusy(false);
  };

  useEffect(() => {
    const t = setTimeout(refresh, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, includeDrink, promo]);

  return (
    <div data-testid="wallet-topup-widget"
         style={{ background: "#fff", border: "1px solid #ecdeb7",
                    borderRadius: 18, padding: "26px 26px",
                    boxShadow: "0 20px 44px rgba(0,0,0,.08)" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center",
                     marginBottom: 6, flexWrap: "wrap" }}>
        <Wallet size={22} color="#b08a3e" />
        <h3 style={{ margin: 0, fontFamily: "Georgia,serif", fontSize: 22, color: "#1a1408" }}>
          Top up your match-day wallet at checkout
        </h3>
      </div>
      <p style={{ margin: "0 0 18px", color: "#5a4a1a", fontSize: 14, lineHeight: 1.55 }}>
        Bolted onto every HustleTickets / Ticketek / Ticketmaster
        checkout — fans load credit alongside their tickets and skip
        the gate-day ATM queue. Promo codes and a "first drink on us"
        voucher preview live below.
      </p>

      {/* Amount picker */}
      <div data-testid="topup-amount-strip"
           style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {AMOUNT_PRESETS.map((n) => {
          const active = n === amount;
          return (
            <button key={n}
                      data-testid={`topup-amount-${n}`}
                      onClick={() => setAmount(n)}
                      style={{
                        border: active ? "2px solid #b08a3e" : "1px solid #ecdeb7",
                        background: active ? "#fff8e6" : "#fff",
                        padding: "8px 16px", borderRadius: 999,
                        fontSize: 14, fontWeight: 700, color: "#1a1408",
                        cursor: "pointer",
                      }}>
              ${n}
            </button>
          );
        })}
        <input data-testid="topup-amount-custom"
                 type="number" min="10" max="10000"
                 value={amount}
                 onChange={(e) => setAmount(Math.max(10, parseInt(e.target.value || "10", 10)))}
                 style={{
                   width: 100, padding: "8px 12px",
                   border: "1px solid #ecdeb7", borderRadius: 999,
                   fontSize: 14, textAlign: "center",
                 }} />
      </div>

      <div style={{ display: "grid", gap: 12,
                     gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                     marginBottom: 14 }}>
        <label data-testid="topup-drink-toggle"
                 style={{ display: "flex", alignItems: "center", gap: 8,
                            fontSize: 13, color: "#3a2f18", fontWeight: 600,
                            cursor: "pointer" }}>
          <input type="checkbox" checked={includeDrink}
                   onChange={(e) => setIncludeDrink(e.target.checked)} />
          Add "first drink on us" voucher (+$12)
        </label>
        <label style={{ fontSize: 12, color: "#3a2f18", fontWeight: 600, display: "block" }}>
          Promo code
          <input data-testid="topup-promo-input"
                   value={promo} onChange={(e) => setPromo(e.target.value)}
                   placeholder="MATCHDAY10 or FIRSTLOAD"
                   style={{
                     width: "100%", padding: "9px 12px", marginTop: 4,
                     border: "1px solid #ecdeb7", borderRadius: 8,
                     fontSize: 13, background: "#fff", color: "#1a1408",
                   }} />
        </label>
      </div>

      {/* Preview */}
      {preview && (
        <div data-testid="topup-preview-card"
             style={{ background: "#fff8e6", border: "1px solid #ecdeb7",
                       borderRadius: 12, padding: "16px 18px",
                       marginBottom: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13,
                          color: "#3a2f18", marginBottom: 6 }}>
            <span>Card charge</span>
            <strong data-testid="topup-preview-amount">${preview.amount_aud.toFixed(2)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13,
                          color: "#1f7a1f", marginBottom: 6 }}>
            <span>Promo credit {preview.promo_applied ? `(${preview.promo_applied})` : ""}</span>
            <strong data-testid="topup-preview-promo">+ ${preview.promo_credit_aud.toFixed(2)}</strong>
          </div>
          {preview.first_drink_aud > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13,
                            color: "#1f7a1f", marginBottom: 6 }}>
              <span>First-drink voucher</span>
              <strong data-testid="topup-preview-drink">+ ${preview.first_drink_aud.toFixed(2)}</strong>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between",
                          fontSize: 15, fontWeight: 700, color: "#1a1408",
                          marginTop: 10, paddingTop: 10,
                          borderTop: "1px dashed #ecdeb7" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <HandCoins size={16} color="#b08a3e" /> Wallet credit
            </span>
            <span data-testid="topup-preview-total">${preview.total_credit_aud.toFixed(2)}</span>
          </div>
          {preview.effective_bonus_pc > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#1f7a1f",
                            display: "flex", alignItems: "center", gap: 4 }}>
              <Sparkles size={12} />
              {preview.effective_bonus_pc.toFixed(1)}% effective bonus on your top-up
            </div>
          )}
        </div>
      )}
      {busy && !preview && (
        <div style={{ fontSize: 12, color: "#7a6b3f" }}>Calculating…</div>
      )}
      <p style={{ marginTop: 14, fontSize: 12, color: "#7a6b3f", lineHeight: 1.5 }}>
        Promo codes shown are demo defaults. Production venues configure
        their own promo table in the operator console — each code caps
        at a per-fan match-day limit for RSA and fraud compliance.
      </p>
    </div>
  );
}
